import { useEffect, useState } from "react";
import {
  ArrowRight,
  CreditCard,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { CreditsSection } from "../memberships/CreditsSection";
import { MembershipDashboardSkeleton } from "../memberships/MembershipDashboardSkeleton";
import { PaymentHistorySection } from "../memberships/paymentHistory";
import { PlanSection } from "../memberships/PlanSection";
import { useAuth } from "../../context/AuthContext";
import { membershipStatsAxios } from "../../services/membership/MembershipAxios";
import type { MembershipData } from "../../types/membership";
import { Language } from "../../utils/dictionary";

const copy = {
  title: {
    [Language.SPANISH]: "Membresía",
    [Language.ENGLISH]: "Membership",
    [Language.PORTUGUESE]: "Assinatura",
  },
  noMembership: {
    [Language.SPANISH]: "No tienes una membresía activa",
    [Language.ENGLISH]: "You do not have an active membership",
    [Language.PORTUGUESE]: "Você não tem uma assinatura ativa",
  },
  choosePlan: {
    [Language.SPANISH]: "Ver planes",
    [Language.ENGLISH]: "View plans",
    [Language.PORTUGUESE]: "Ver planos",
  },
  emptyPayments: {
    [Language.SPANISH]: "Aún no hay pagos para mostrar.",
    [Language.ENGLISH]: "There are no payments to show yet.",
    [Language.PORTUGUESE]: "Ainda não há pagamentos para mostrar.",
  },
};

let cachedMembershipStats: MembershipData | null = null;

export function MembershipSettingsPanel() {
  const { language } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<MembershipData | null>(cachedMembershipStats);
  const [isLoading, setIsLoading] = useState(!cachedMembershipStats);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        if (!cachedMembershipStats) setIsLoading(true);
        const response = await membershipStatsAxios();
        cachedMembershipStats = response;
        if (mounted) setStats(response);
      } catch (error) {
        console.error("Error fetching membership stats:", error);
        if (mounted) setStats(null);
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const plan = stats?.active_membership;
  const credits = stats?.details;
  const payments = stats?.payments ?? [];

  if (isLoading) {
    return (
      <div>
        <header className="mb-5">
          <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
            {copy.title[language]}
          </h1>
        </header>
        <MembershipDashboardSkeleton variant="compact" />
      </div>
    );
  }

  if (!stats || !plan) {
    return (
      <div>
        <header className="mb-5">
          <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
            {copy.title[language]}
          </h1>
        </header>
        <div className="flex min-h-[360px] items-center justify-center py-8">
          <div className="max-w-md text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-500">
              <CreditCard className="h-6 w-6" />
            </div>
            <h2 className="text-xl font-semibold text-gray-950">
              {copy.noMembership[language]}
            </h2>
            <button
              type="button"
              onClick={() => navigate("/plans")}
              className="mt-5 inline-flex h-11 items-center gap-2 rounded-xl bg-gray-950 px-4 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
            >
              {copy.choosePlan[language]}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-5">
        <h1 className="text-2xl font-semibold tracking-normal text-gray-950">
          {copy.title[language]}
        </h1>
      </header>

      <section className="min-w-0">
        <PlanSection plan={plan} variant="compact" />
        {credits && (
          <CreditsSection
            credits={credits}
            nextRenewalDate={plan.payment_ends_at || ""}
            variant="compact"
          />
        )}
        {payments.length > 0 ? (
          <PaymentHistorySection payments={payments} variant="compact" />
        ) : (
          <div className="border-y border-gray-100 py-4 text-sm text-gray-500">
            {copy.emptyPayments[language]}
          </div>
        )}
      </section>
    </div>
  );
}
